import { useState } from 'react';
import { AdminSidebar } from '@/components/Layout/AdminSidebar';
import { useAuthStore } from '@/store/authStore';
import { useTheme } from '@/hooks/useTheme';
import { SuperAdminPageHelp } from '@/components/SuperAdmin/SuperAdminPageHelp';
import { SimpleCatalogTab } from '@/components/MetricCatalog/SimpleCatalogTab';
import { MetricsTab } from '@/components/MetricCatalog/MetricsTab';
import { VariantsTab } from '@/components/MetricCatalog/VariantsTab';
import { MappingsTab } from '@/components/MetricCatalog/MappingsTab';
import { CheckMappingTab } from '@/components/MetricCatalog/CheckMappingTab';

type Tab = 'domains' | 'valueTypes' | 'units' | 'metrics' | 'variants' | 'mappings' | 'checkMapping';

export function MetricCatalogPage() {
  const { user } = useAuthStore();
  const { isDark, toggleTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<Tab>('domains');

  const getUserInitials = (name: string) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const tabs: { id: Tab; label: string }[] = [
    { id: 'domains', label: 'Domains' },
    { id: 'valueTypes', label: 'Value Types' },
    { id: 'units', label: 'Units' },
    { id: 'metrics', label: 'Metrics' },
    { id: 'variants', label: 'Variants' },
    { id: 'mappings', label: 'Attribute Type Mapping' },
    { id: 'checkMapping', label: 'Check mapping' },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-slate-50 dark:bg-[#0B1116] text-slate-800 dark:text-slate-200 transition-colors duration-200">
      <AdminSidebar getUserInitials={getUserInitials} />

      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-slate-50 dark:bg-[#0B1116]">
        <header className="flex items-center justify-between h-16 px-6 border-b border-slate-200 dark:border-[#1E293B] bg-white dark:bg-[#16202A]">
          <div className="hidden md:block text-sm text-slate-500 dark:text-slate-400">
            <span className="text-slate-400 dark:text-slate-600">Administration</span> / Metric Catalog
          </div>
          <div className="flex items-center gap-4">
            <SuperAdminPageHelp />
            <button
              onClick={toggleTheme}
              className="p-2 rounded-md text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-gray-800 transition-colors"
              aria-label="Toggle theme"
            >
              <span className="material-icons-outlined">{isDark ? 'light_mode' : 'dark_mode'}</span>
            </button>
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400">
              <span className="material-icons-outlined text-lg">person</span>
              <span>{user?.fullName || 'Admin'}</span>
            </div>
          </div>
        </header>

        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-6">Metric Catalog (UI v2)</h1>

            {/* Tabs */}
            <div className="border-b border-slate-200 dark:border-[#1E293B] mb-6">
              <nav className="flex space-x-1" aria-label="Tabs">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
                      activeTab === tab.id
                        ? 'bg-primary/10 text-primary dark:text-accent border-b-2 border-primary dark:border-accent'
                        : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200 hover:bg-slate-100 dark:hover:bg-gray-800'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            </div>

            {/* Tab Content */}
            <div>
              {activeTab === 'domains' && (
                <SimpleCatalogTab type="domains" title="Measurement Domains" itemLabel="Domaine" />
              )}
              {activeTab === 'valueTypes' && (
                <SimpleCatalogTab type="valueTypes" title="Value Types" itemLabel="Type de valeur" />
              )}
              {activeTab === 'units' && <SimpleCatalogTab type="units" title="Units" itemLabel="Unité" />}
              {activeTab === 'metrics' && <MetricsTab />}
              {activeTab === 'variants' && <VariantsTab />}
              {activeTab === 'mappings' && <MappingsTab />}
              {activeTab === 'checkMapping' && <CheckMappingTab />}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
